import React from "react";

/**
 * Cinematic intro mask + scroll reveal + magnetic buttons.
 * Faithful port of approved Hostinger script.js (pageMask + reveal + magnetic).
 *
 * - <div class="page-mask"> covers the screen on load; we add `.gone` after
 *   60ms so the CSS clip-path circle reveal animation kicks in (1.6s).
 * - All `.rv` elements fade-in via IntersectionObserver as they enter view.
 * - Buttons with [data-magnetic] gently follow the cursor on hover.
 */
export default function CinematicLayer({ active = true }) {
  React.useEffect(() => {
    if (!active) return;
    // ---- pageMask: cinematic cosmic-iris intro ----
    const m = document.getElementById("pageMask");
    let removeT;
    let startTimer;
    if (m) {
      const fire = () => {
        requestAnimationFrame(() => {
          m.classList.add("gone");
          removeT = setTimeout(() => m.remove(), 2400);
        });
      };
      // Hold the dark mask for ~700ms so the user actually SEES the iris reveal,
      // not just a 60ms flash. Total intro feel ≈ 700ms hold + 1.6s iris = 2.3s.
      startTimer = setTimeout(fire, 700);
    }

    // ---- reveal: stagger then IntersectionObserver ----
    document.querySelectorAll("[data-stagger]").forEach((parent) => {
      const step = parseInt(parent.getAttribute("data-stagger") || "70", 10);
      const cap = parseInt(parent.getAttribute("data-stagger-cap") || "8", 10);
      Array.from(parent.children).forEach((child, i) => {
        if (child.classList.contains("rv") || child.querySelector(".rv")) {
          const target = child.classList.contains("rv") ? child : child.querySelector(".rv");
          target.style.setProperty("--rv-d", `${Math.min(i, cap) * step}ms`);
        }
      });
    });
    const revealEls = document.querySelectorAll(".rv");
    let io;
    if (revealEls.length) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
      );
      revealEls.forEach((el) => io.observe(el));
    }

    // ---- magnetic buttons (desktop only) ----
    const reduce =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(pointer:coarse)").matches;
    const magneticHandlers = [];
    if (!reduce) {
      document.querySelectorAll("[data-magnetic]").forEach((el) => {
        const STR = parseFloat(el.getAttribute("data-magnetic") || "0.35");
        let raf = null;
        const move = (e) => {
          const r = el.getBoundingClientRect();
          const dx = (e.clientX - (r.left + r.width / 2)) * STR;
          const dy = (e.clientY - (r.top + r.height / 2)) * STR;
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            el.style.transform = `translate(${dx.toFixed(1)}px,${dy.toFixed(1)}px)`;
          });
        };
        const leave = () => {
          if (raf) cancelAnimationFrame(raf);
          el.style.transform = "translate(0,0)";
        };
        el.addEventListener("pointermove", move);
        el.addEventListener("pointerleave", leave);
        magneticHandlers.push([el, move, leave]);
      });
    }

    return () => {
      clearTimeout(startTimer);
      clearTimeout(removeT);
      if (io) io.disconnect();
      magneticHandlers.forEach(([el, move, leave]) => {
        el.removeEventListener("pointermove", move);
        el.removeEventListener("pointerleave", leave);
      });
    };
  }, [active]);

  return (
    <div className="page-mask" id="pageMask" aria-hidden="true" />
  );
}
